// packages/ui/src/tools/ToolRegistry.ts
import type { PlatformBridge, ToolCapability } from '@slate/core';
import type { ToolDefinition } from './types';

const defs = new Map<string, ToolDefinition>();
const listeners = new Set<() => void>();

/**
 * Per-point cache of the flattened contributions array. `contributionsFor`
 * (consumed via `useSyncExternalStore` in `useToolContributionsFor`) MUST return
 * a referentially-stable value while the registry is unchanged — rebuilding a
 * fresh array every call makes React re-render forever. The cache is cleared on
 * every registry mutation (see `emit`), so a stable snapshot is returned between
 * mutations and a new one only after a register/unregister.
 */
const contributionsCache = new Map<string, unknown[]>();

function emit() {
  contributionsCache.clear();
  listeners.forEach((l) => l());
}

export const ToolRegistry = {
  // Generic so a concrete `ToolDefinition<TState>` (e.g. the terminal's
  // `ToolDefinition<TerminalState>`) registers without a cast. The registry
  // stores type-erased `ToolDefinition<unknown>` since it holds heterogeneous
  // tools keyed by id; per-state safety is preserved at each tool's authoring
  // and load() site.
  register<TState>(def: ToolDefinition<TState>): () => void {
    if (defs.has(def.id)) {
      throw new Error(`ToolRegistry: duplicate tool id "${def.id}"`);
    }
    defs.set(def.id, def as ToolDefinition);
    emit();
    return () => {
      defs.delete(def.id);
      emit();
    };
  },
  getAll(): readonly ToolDefinition[] {
    return Array.from(defs.values());
  },
  getById(id: string): ToolDefinition | undefined {
    return defs.get(id);
  },
  getAvailable(platform: PlatformBridge): readonly ToolDefinition[] {
    const caps = (platform.capabilities ?? {}) as unknown as Record<
      string,
      boolean | undefined
    >;
    return Array.from(defs.values()).filter((d) =>
      (d.capabilities ?? []).every((c: ToolCapability) => caps[c] === true),
    );
  },
  contributionsFor<K extends keyof import('@slate/core').ToolContributions>(
    point: K,
  ): NonNullable<import('@slate/core').ToolContributions[K]> {
    // Return the cached snapshot when the registry hasn't changed — keeps the
    // reference stable for `useSyncExternalStore` consumers (see cache comment).
    const cached = contributionsCache.get(point as string);
    if (cached) {
      return cached as NonNullable<import('@slate/core').ToolContributions[K]>;
    }
    // For array-valued contribution points, flatten and concatenate.
    // For object-valued points (e.g., closeGuard), return the first one we see.
    const out: unknown[] = [];
    for (const def of defs.values()) {
      const c = def.contributions as Record<string, unknown> | undefined;
      const items = c?.[point as string];
      if (Array.isArray(items)) out.push(...items);
      else if (items != null) out.push(items);
    }
    contributionsCache.set(point as string, out);
    return out as NonNullable<import('@slate/core').ToolContributions[K]>;
  },
  subscribe(cb: () => void): () => void {
    listeners.add(cb);
    return () => listeners.delete(cb);
  },
  /** Test-only: clear all registered definitions. */
  _resetForTest(): void {
    defs.clear();
    emit();
  },
};
